import {Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
    AbstractStylizableComponent,
    AccountNameProvider,
    ColorizableComponent,
    ColorizableTextComponent,
    DefaultProfile,
    EntityConfigService,
    ExtendedWorkspace,
    LocaleService,
    LogoService,
    PesAccountService,
    PesAppHeaderStyleRule,
    PesAuthService,
    PesStyle,
    PesStyleService,
    PesStyleType,
    ScrollService,
    SmartUIService,
    StateProvider,
    SubscriptionManager
} from '@pes/ui';
import {StateService} from '@uirouter/angular';
import {ISubscription} from 'rxjs/Subscription';
import {NgbDropdown} from '@ng-bootstrap/ng-bootstrap';
import {DOCUMENT} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'lbpa-app-header',
    templateUrl: 'lbpa-app-header.component.html',
    styleUrls: ['./lbpa-app-header.component.scss']
})
export class LbpaAppHeaderComponent extends AbstractStylizableComponent<PesAppHeaderStyleRule>
    implements ColorizableComponent, ColorizableTextComponent, OnInit, OnDestroy {

    /**
     * The title of the application.
     */
    @Input() public label: string = null;
    /**
     * The height of the application logo.
     */
    @Input() public logoHeight: number = 50;
    /**
     * Indicates whether to display the locale button (true), or not (false).
     */
    @Input() public showLocaleButton: boolean = false;
    /**
     * Indicates whether to display the workflow button (true), or not (false).
     */
    @Input() public showWorkflowButton: boolean = false;

    @Input() public showHeader: boolean = false;
    /**
     * The path to the logo URL stored in the file system.
     */
    protected logoUrl: string = null;
    /**
     * The reference to the PES account service.
     */
    protected readonly account: PesAccountService = null;
    /**
     * The style object used by the ngStyle directive to set custom styles.
     */
    protected customStyles: any = null;
    /**
     * Indicates whether the component is collapsed (true), or not (false).
     */
    protected isCollapsed: boolean = true;
    /**
     * The options of the smartUI config.
     */
    protected extendedWorkspace: ExtendedWorkspace = null;
    /**
     * The reference to the PES authentication service.
     */
    private readonly _auth: PesAuthService = null;
    /**
     * The reference to the PES styles service.
     */
    private readonly _styleService: PesStyleService = null;
    /**
     * The reference to the PES locale service.
     */
    private readonly _locale: LocaleService = null;
    /**
     * The reference to the Document element.
     */
    private readonly _document: Document = null;
    /**
     * Reference of PES logo service.
     */
    private readonly _logoService: LogoService = null;
    /**
     * Reference of PES smartUI service.
     */
    private readonly _smartUIService: SmartUIService = null;
    /**
     * Reference of PES entity configuration service.
     */
    private readonly _entityConfigService: EntityConfigService = null;
    /**
     * Reference of PES StateProvider service.
     */
    private readonly _stateProvider: StateProvider = null;
    /**
     * Reference of PES AccountNameProvider service.
     */
    private readonly _accountNameProvider: AccountNameProvider = null;

    private readonly _translate: TranslateService = null;
    /**
     * The reference to the StateService service.
     */
    private readonly _state: StateService = null;

    private readonly DAYS_BEFORE: number = 10;

    /**
     * Creates a new PesAppHeaderComponent instance.
     *
     * @param {Injector} injector the reference to the Angular injector.
     */
    constructor(protected injector: Injector) {
        super();
        this.account = injector.get(PesAccountService);
        this._auth = injector.get(PesAuthService);
        this._styleService = injector.get(PesStyleService);
        this._locale = injector.get(LocaleService);
        this._document = injector.get(DOCUMENT);
        this._logoService = injector.get(LogoService);
        this._stateProvider = injector.get(StateProvider);
        this._accountNameProvider = injector.get(AccountNameProvider);
        this._translate = injector.get(TranslateService);
        this._state = injector.get(StateService);
        this._entityConfigService = injector.get(EntityConfigService);
        SubscriptionManager.getInstance().createSubscribable(this);
        const sub: ISubscription = this._logoService.onLogoChange.subscribe((newLogo: string) => {
            if (this.logoUrl !== newLogo) {
                this.logoUrl = newLogo;
            }
        });
        SubscriptionManager.getInstance().registerSubscription(this, sub);
        this._smartUIService = injector.get(SmartUIService);
        this.extendedWorkspace = this._smartUIService.extendedWorkspace;
        const sub2: ISubscription = this._smartUIService.onExtendedChange.subscribe((options: ExtendedWorkspace) => {
            if (this.extendedWorkspace !== options) {
                this.extendedWorkspace = options;
            }
        });
        SubscriptionManager.getInstance().registerSubscription(this, sub2);
    }

    /**
     * The background color for this component.
     */
    private _backgroundColor: any = null;

    public get backgroundColor(): any {
        return this._backgroundColor;
    }

    /**
     * @inheritDoc
     */
    @Input()
    public set backgroundColor(value: any) {
        this._backgroundColor = value;
        this.initStyles();
    }

    public get logo(): string {
        return this._logoService.appLogo;
    }

    /**
     * Gets or sets the application logo.
     */
    @Input()
    public set logo(value: string) {
        this._logoService.appLogo = this.logoUrl = value;
    }

    /**
     * The text color for this component.
     */
    private _color: any = null;

    public get color(): any {
        return this._color;
    }

    /**
     * @inheritDoc
     */
    @Input()
    public set color(value: any) {
        this._color = value;
        this.initStyles();
    }

    /**
     * The text size for this component.
     */
    private _fontSize: string = null;

    public get fontSize(): string {
        return this._fontSize;
    }

    /**
     * @inheritDoc
     */
    @Input()
    public set fontSize(value: string) {
        this._fontSize = value;
        this.initStyles();
    }

    /**
     * The text weight for this component.
     */
    private _fontWeight: string = null;

    public get fontWeight(): string {
        return this._fontWeight;
    }

    /**
     * @inheritDoc
     */
    @Input()
    public set fontWeight(value: string) {
        this._fontWeight = value;
        this.initStyles();
    }

    /**
     * @inheritDoc
     */
    @Input()
    public set style(value: PesStyle) {
        if (value) {
            const rules: PesAppHeaderStyleRule = value.rules;
            this._color = rules.color;
            this._fontSize = rules.fontSize;
            this._fontWeight = rules.fontWeight;
            this._backgroundColor = rules.backgroundColor;
        }
        this.initPesStyle(value);
    }

    /**
     * @inheritDoc
     */
    public ngOnInit(): void {
        const sub: ISubscription = this._styleService.styleChanged.subscribe(this.checkStyles.bind(this));
        SubscriptionManager.getInstance().registerSubscription(this, sub);
    }

    /**
     * @inheritDoc
     */
    public ngOnDestroy(): void {
        SubscriptionManager.getInstance().deleteSubscribable(this);
    }


    public closeAlertExpiration(): void {
        sessionStorage.setItem('alertExpiration', 'hide');
    }

    public checkExpiration(): boolean {
        return !sessionStorage.getItem('alertExpiration') ? this.account.getExpirationDate(this.DAYS_BEFORE) : false;
    }

    protected getPasswordAlert(): string {
        const dateLimit = this.account.getExpirationDate(this.DAYS_BEFORE);
        return this._translate.instant('password.expiration', {
            days: Math.floor(dateLimit.days)
        });
    }

    /**
     * Returns the name to be displayed for the associated account.
     *
     * @returns {string} the name to be displayed for the associated account.
     */
    protected getAccountNameDisplay(): string {
        return this._accountNameProvider.getAccountNameDisplay();
    }

    /**
     * Invoked when the user clicks the extended workspace button.
     */
    protected changeFullScreen(): void {
        this._smartUIService.statusExtendedWorkspace = !this.extendedWorkspace.displayed;
    }

    /**
     * Invoked when the user clicks the logout button.
     */
    protected logout(): void {
        this._auth.logout();
        this._stateProvider.logoutState();
    }

    /**
     * @inheritDoc
     */
    protected initStyles(): void {
        this.customStyles = {};
        this.setStyle(this.customStyles, 'color', this.color);
        this.setStyle(this.customStyles, 'background-color', this.backgroundColor);
        this.setStyle(this.customStyles, 'font-weight', this.fontWeight);
        this.setStyle(this.customStyles, 'font-size', this.fontSize);
    }

    /**
     * For RGAA - Invoked each time user click on 'skip-content' link.
     *
     * @param {string} anchorId this id of the element to scroll and focus on.
     */
    protected scrollToId(anchorId: string): void {
        const scrollService: ScrollService = this.injector.get(ScrollService);
        const elm = this._document.getElementById(anchorId);
        if (elm !== null) {
            scrollService.scroll(elm, true);
            elm.focus();
        }
    }

    /**
     * Goes to the app main page.
     */
    protected goToHome(): void {
        this._stateProvider.goToHome();
    }

    /**
     * Goes to the specified state view.
     *
     * @param {string} state the reference of the state to go.
     */
    protected goToState(state: string): void {
        if (this._state.current.name !== state) {
            this._state.go(state);
        }
    }

    /**
     * Toggle NgDropdown component and prevent event propagation.
     *
     * @param {any} event the event fire.
     * @param {NgbDropdown} dropdown the dropdown object.
     */
    protected toggleDropdown(event: any, dropdown: NgbDropdown): void {
        event.stopPropagation();
        event.preventDefault();
        dropdown.toggle();
    }

    protected onLogoClick(): void {
        if (this.account.hasProfile(DefaultProfile.VIRTUAL_USER)) {
            this._entityConfigService.getEntity().subscribe((cfg: any) => {
                window.location.href = cfg.logoRedirectUrl;
            });
            // } else {
            //     this._state.go(DefaultState.DASHBOARD);
        }
    }

    /**
     * Initializes the PES styles for this component.
     *
     * @param {PesStyle[]} styles the list of PES styles for this component.
     */
    private checkStyles(styles: PesStyle[]): void {
        if (styles) {
            this.style = styles.find((elm: PesStyle) => {
                return elm.type === PesStyleType.HEADER;
            });
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/component/app-header/lbpa-app-header.component.ts